/**
 * Excel Utilities
 *
 * Client-side utilities for working with Excel files.
 * Uses dynamic import to lazy-load the xlsx library only when needed.
 *
 * @module lib/excelUtils
 */

/**
 * Check if a file is an Excel file based on extension
 */
export function isExcelFile(file: File): boolean {
  const extension = file.name.split('.').pop()?.toLowerCase();
  return extension === 'xlsx' || extension === 'xls';
}

/**
 * Get sheet names from an Excel file
 *
 * Uses dynamic import to lazy-load the xlsx library (~150KB).
 * Only loads the library when this function is called.
 *
 * @param file - Excel file to extract sheet names from
 * @returns Array of sheet names in the workbook (empty array if no sheets)
 * @throws Error if file cannot be read, is corrupted, or is not a valid Excel file
 *
 * @example
 * ```tsx
 * const file = event.target.files[0];
 * if (isExcelFile(file)) {
 *   try {
 *     const sheets = await getExcelSheetNames(file);
 *     console.log('Available sheets:', sheets);
 *   } catch (error) {
 *     console.error('Failed to read Excel file:', error);
 *   }
 * }
 * ```
 */
export async function getExcelSheetNames(file: File): Promise<string[]> {
  try {
    // Lazy load xlsx library
    const XLSX = await import('xlsx');

    // Read file as ArrayBuffer
    const buffer = await file.arrayBuffer();

    // Parse workbook with minimal options (only need sheet names)
    const workbook = XLSX.read(buffer, {
      type: 'array',
      bookSheets: true, // Only parse sheet names, not data
    });

    return workbook.SheetNames;
  } catch (error) {
    console.error('Failed to read Excel sheet names:', error);
    throw new Error(
      `Failed to read Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}
